import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {IconButton, Flag} from 'platform/components';
import {Box, HStack, Icon, Text, VStack, Show} from 'platform/foundation';
import styled from 'styled-components';

import {isEmpty} from 'ramda';

import {RelativeFilterResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, getSafely} from 'shared/web';

import {useApiEnums} from '../../../hooks/useApiEnums';
import {useFeatureLabels} from '../../../hooks/useFeatureLabels';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {getPresetTags} from '../../RelativeFiltersPresetEditor/utils/getPresetTags';

interface RelativeFilterPresetListItemProps extends TestIdProps {
  preset: RelativeFilterResponseBody;
  isSortable: boolean;
  onOpen: () => void;
  onEdit: () => void;
}

export function RelativeFilterPresetListItem(props: RelativeFilterPresetListItemProps) {
  const {preset, isSortable, onOpen, onEdit} = props;
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: preset.id,
    disabled: !isSortable,
  });
  const dragHandleAttributes = isSortable ? attributes : undefined;
  const dragHandleListeners = isSortable ? listeners : undefined;
  const sourcingCurrency = useSourcingCurrency();
  const {getEnum} = useApiEnums();
  const {getFeatureLabel, isKnownFeature} = useFeatureLabels();
  const sellerTypeLabels = Object.fromEntries(
    getEnum('seller_type').map((item) => [item.key, item.label])
  );
  const tags = getPresetTags(preset.config, {
    currencyCode: sourcingCurrency?.code,
    getSellerTypeLabel: (key) => getSafely(sellerTypeLabels, key, key),
    getFeatureLabel,
    isKnownFeature,
  });

  const row = (
    <RowRoot>
      <OpenButton type="button" onClick={onOpen} data-testid={suffixTestId('open', props)}>
        <HStack spacing={2} align="center">
          <DragHandleSlot>
            <Show when={isSortable}>
              <DragHandle
                {...dragHandleAttributes}
                {...dragHandleListeners}
                onClick={(event) => event.stopPropagation()}
              >
                <Icon value="action/drag_indicator" color="palettes.neutral.60.100" size={4} />
              </DragHandle>
            </Show>
          </DragHandleSlot>
          <Box flexGrow={1} minWidth={0}>
            <VStack spacing={1}>
              <HStack spacing={1} align="center" wrap>
                <Text size="small" alternative>
                  {preset.name}
                </Text>
                <Show when={preset.default}>
                  <Flag
                    label={i18n.t('entity.sourcing.relativeFilters.defaultBadge')}
                    size="small"
                    colorScheme="blue"
                    isSubtle
                  />
                </Show>
              </HStack>
              <Show when={!isEmpty(tags)}>
                <Text size="xSmall" color="secondary" noWrap>
                  {tags.map((tag) => tag.label).join(', ')}
                </Text>
              </Show>
            </VStack>
          </Box>
        </HStack>
      </OpenButton>
      <IconButton
        icon="image/edit"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onEdit();
        }}
        data-testid={suffixTestId('edit', props)}
      />
    </RowRoot>
  );

  if (!isSortable) {
    return row;
  }

  return (
    <SortableRow
      ref={setNodeRef}
      $transform={CSS.Transform.toString(transform) ?? undefined}
      $transition={transition ?? undefined}
      $isDragging={isDragging}
    >
      {row}
    </SortableRow>
  );
}

const RowRoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(1)};
  padding: ${({theme}) => theme.getSize(1)};
  border-radius: ${({theme}) => theme.radii.small};

  &:hover,
  &:focus-within {
    background-color: ${({theme}) => theme.colors.palettes.blue[10][100]};
  }
`;

const OpenButton = styled.button`
  display: block;
  flex: 1;
  min-width: 0;
  padding: ${({theme}) => theme.getSize(1)};
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;

  &:focus-visible {
    outline: none;
  }
`;

const DragHandleSlot = styled.div`
  width: ${({theme}) => theme.getSize(4)};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  opacity: 0;

  ${RowRoot}:hover &,
  ${RowRoot}:focus-within & {
    opacity: 1;
  }
`;

const SortableRow = styled.div<{
  $transform: string | undefined;
  $transition: string | undefined;
  $isDragging: boolean;
}>`
  transform: ${({$transform}) => $transform};
  transition: ${({$transition}) => $transition};
  opacity: ${({$isDragging}) => ($isDragging ? 0.5 : 1)};
`;
